import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  toAmountBucket,
  trackOnboardingError,
  trackOnboardingEvent,
} from "../analytics/onboarding";
import { trackFinancialError, trackFinancialEvent } from "../analytics/financial";
import type { FormChatState } from "../repositories/formChat.repository";
import {
  buildFinancialTransactionPayload,
  type FinancialChatFlow,
} from "../utils/financialFormChatPayloadBuilder";
import { notifyLimitReached } from "../utils/limitToasts";
import { readReturnPath } from "../utils/navigationOrigin";
import { toast } from "../utils/toast";
import { useCachedMedium } from "./useCachedMedium";
import { useCachedTerreiro } from "./useCachedTerreiro";
import { useCreateIncomingMutation } from "./useCreateIncomingMutation";
import { useCreateOutgoingMutation } from "./useCreateOutgoingMutation";
import { useEditIncomeMutation } from "./useEditIncomeMutation";
import { useEditOutgoingMutation } from "./useEditOutgoingMutation";
import { useFeatureLimit } from "./useFeatureLimit";
import { useProductAnalyticsBase } from "./useProductAnalyticsContext";

const SOURCE_PAGE = "FormChatAssistant";

export class FinancialChatSubmitError extends Error {}

/** Returns whether a stock-specific toast was shown. */
const notifyInventoryPurchase = (
  payload: { inventoryItemId?: string; purchasedQuantity?: number; value: number; settlementStatus?: string },
  isEdit: boolean,
): boolean => {
  const linkedItemId = payload.inventoryItemId;
  const quantity = Number(payload.purchasedQuantity);
  const hasQuantity = Number.isInteger(quantity) && quantity >= 1;
  const isPaid = payload.settlementStatus === "CONSOLIDATED";

  if (isEdit) {
    if (linkedItemId && hasQuantity && isPaid) {
      toast.success("Item de estoque atualizado com esta compra.");
      return true;
    }
    return false;
  }

  if (!linkedItemId) {
    return false;
  }

  if (hasQuantity && isPaid) {
    const unitPrice = payload.value / quantity;
    toast.success("Compra registrada no estoque.", {
      description: Number.isFinite(unitPrice)
        ? `A quantidade e o preço unitário (${unitPrice.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}) foram atualizados no item.`
        : "A quantidade e o preço unitário foram atualizados no item.",
    });
  } else if (hasQuantity) {
    toast.success("Compra vinculada ao item.", {
      description:
        "A quantidade e o preço entram no estoque quando a despesa for marcada como paga.",
    });
  } else {
    toast.success("Compra vinculada ao item.", {
      description:
        "Informe a quantidade comprada para atualizar o estoque e o preço unitário.",
    });
  }
  return true;
};

const notifyTransactionSaved = (isIncome: boolean, isEdit: boolean) => {
  if (isEdit) {
    toast.success(isIncome ? "Entrada atualizada." : "Saída atualizada.");
    return;
  }
  toast.success(isIncome ? "Entrada registrada." : "Saída registrada.");
};

export function useFormChatSubmitFinancial({
  flow,
  editTargetId,
  enabled = true,
}: {
  flow: FinancialChatFlow;
  editTargetId?: string;
  enabled?: boolean;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const productBase = useProductAnalyticsBase();
  const terreiroId = useCachedTerreiro()?.id;
  const mediumId = useCachedMedium()?.id;
  const isIncome = flow === "income";
  const isEdit = Boolean(editTargetId);

  const limit = useFeatureLimit({
    feature: isIncome ? "maxIncomingTransactions" : "maxOutcomeTransactions",
    enabled,
  });
  const createIncoming = useCreateIncomingMutation(terreiroId, mediumId);
  const createOutgoing = useCreateOutgoingMutation(terreiroId, mediumId);
  const editIncome = useEditIncomeMutation(editTargetId ?? "");
  const editOutgoing = useEditOutgoingMutation(editTargetId ?? "");

  const navigateBack = useCallback(() => {
    navigate(
      readReturnPath(location.state) ??
        (isIncome ? "/financial/income" : "/financial/outcome"),
    );
  }, [isIncome, location.state, navigate]);

  const submitFinancialByChat = useCallback(
    async (state: FormChatState) => {
      const values = buildFinancialTransactionPayload(state, flow);
      if (!values) {
        throw new FinancialChatSubmitError(
          "Revise os dados do lançamento no chat antes de confirmar.",
        );
      }
      const owner = terreiroId ? { terreiroId } : { mediumId };
      const transactionType = isIncome ? "income" : "outcome";
      const analyticsBase = { ...productBase, trigger: "mutation" as const };

      if (isEdit && editTargetId) {
        const updateEvents = isIncome
          ? {
              attempt: "financial_income_update_submit_attempt",
              success: "financial_income_updated",
              error: "financial_income_update_error",
            } as const
          : {
              attempt: "financial_outcome_update_submit_attempt",
              success: "financial_outcome_updated",
              error: "financial_outcome_update_error",
            } as const;
        const payload = { ...values, ...owner, id: editTargetId };
        trackFinancialEvent(updateEvents.attempt, analyticsBase, {
          transaction_id: editTargetId,
          source: "form_chat",
        });
        try {
          await (isIncome ? editIncome : editOutgoing).mutateAsync(payload);
        } catch (error) {
          trackFinancialError(updateEvents.error, analyticsBase, error, {
            transaction_id: editTargetId,
          source: "form_chat",
          });
          throw error;
        }
        trackFinancialEvent(updateEvents.success, analyticsBase, {
          transaction_id: editTargetId,
          source: "form_chat",
        });
        if (!notifyInventoryPurchase(values, true)) {
          notifyTransactionSaved(isIncome, true);
        }
        navigateBack();
        return;
      }

      const onboardingEvent = isIncome
        ? "onboarding_income_created"
        : "onboarding_outcome_created";
      const onboardingBase = {
        sourcePage: SOURCE_PAGE,
        terreiroId,
        mediumId,
        trigger: "mutation" as const,
      };

      if (!limit.canCreate) {
        trackOnboardingEvent(
          onboardingEvent,
          { ...onboardingBase, success: false, featureLimitBlocked: true },
          { transaction_type: transactionType, source: "form_chat" },
        );
        trackFinancialEvent(
          "financial_transaction_create_submit_attempt",
          { ...analyticsBase, success: false, featureLimitBlocked: true },
          { transaction_type: transactionType, source: "form_chat" },
        );
        notifyLimitReached({
          feature: isIncome ? "maxIncomingTransactions" : "maxOutcomeTransactions",
          message: limit.message,
        });
        throw new FinancialChatSubmitError(
          limit.message || "Limite do plano atingido.",
        );
      }

      const payload = { ...values, ...owner };
      const amountBucket = toAmountBucket(Number(values.value));
      trackFinancialEvent(
        "financial_transaction_create_submit_attempt",
        analyticsBase,
        { transaction_type: transactionType, source: "form_chat" },
      );
      try {
        await (isIncome ? createIncoming : createOutgoing).mutateAsync(payload);
      } catch (error) {
        trackOnboardingError(onboardingEvent, onboardingBase, error, {
          transaction_type: transactionType,
          amount_bucket: amountBucket,
          source: "form_chat",
        });
        trackFinancialError(
          "financial_transaction_create_error",
          analyticsBase,
          error,
          { transaction_type: transactionType, source: "form_chat" },
        );
        throw error;
      }

      trackOnboardingEvent(onboardingEvent, onboardingBase, {
        transaction_type: transactionType,
        amount_bucket: amountBucket,
        source: "form_chat",
      });
      trackFinancialEvent("financial_transaction_created", analyticsBase, {
        transaction_type: transactionType,
        amount_bucket: amountBucket,
        source: "form_chat",
      });
      limit.invalidate();
      if (!notifyInventoryPurchase(values, false)) {
        notifyTransactionSaved(isIncome, false);
      }
      navigateBack();
    },
    [
      createIncoming,
      createOutgoing,
      editIncome,
      editOutgoing,
      editTargetId,
      flow,
      isEdit,
      isIncome,
      limit,
      mediumId,
      navigateBack,
      productBase,
      terreiroId,
    ],
  );

  return {
    submitFinancialByChat,
    navigateBack,
    limit,
    isSubmitting:
      createIncoming.isPending ||
      createOutgoing.isPending ||
      editIncome.isPending ||
      editOutgoing.isPending,
  };
}
